import React, { useEffect, useState } from "react";
import {
  Tab,
  Tabs,
  Box,
  Typography,
  Grid,
  Card,
  Button,
  Dialog,
  Select,
  MenuItem,
  Divider,
  FormControl,
  InputLabel,
  Chip,
} from "@mui/material";
import { styled } from "@mui/system";
import { customerservice, AssetFinanceUrl, setupManagementUrl } from "../../../config";
import {
  executeAnyMutation,
  fetchJsonDynamic,
} from "../../../data/DynamicServiceNew/jsondynamicservice";
import DynamicTableForJson from "../../../data/DynamicTable/dynamicTableForJson";
import Swal from "sweetalert2";
import { formatCurrency, computeLtv, getForcedSaleValue } from "./ApprovalTable";

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

// Row already carries collateral fields joined in from allAssetLoanApplications
const CollateralSummary = ({ Row }) => {
  const fsv = getForcedSaleValue(Row);
  const ltv = computeLtv(Row);
  return (
    <StyledCard variant="outlined">
      <Typography variant="h6" sx={{ color: "#003049", fontWeight: "bold" }}>
        Collateral Summary
      </Typography>
      <Box sx={{ mt: 1 }}>
        <Typography>
          Asset: <strong>{[Row.AssetTypeName, Row.SubTypeName].filter(Boolean).join(" - ") || `#${Row.AssetId || Row.Id}`}</strong>
        </Typography>
        <Typography>
          Forced Sale Value (FSV): <strong>{fsv !== null ? formatCurrency(fsv) : "Not Set"}</strong>
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
          <Typography>Loan-to-Value (LTV):</Typography>
          <Chip
            size="small"
            label={ltv === null ? "N/A" : `${ltv.toFixed(1)}%`}
            color={ltv !== null && ltv > 80 ? "error" : "success"}
          />
        </Box>
      </Box>
    </StyledCard>
  );
};

export const AssetLoanApprovalDialog = ({
  open,
  onClose,
  Row,
  refetchData,
  approvalParameters,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [disbursementMethods, setDisbursementMethods] = useState([]);
  const [disbursementMethodSelected, setDisbursementMethodSelected] = useState("");
  const [loadingDisbursementMethods, setLoadingDisbursementMethods] = useState(false);

  const isCompanyLevel = !!approvalParameters?.isCompany;

  useEffect(() => {
    if (!isCompanyLevel) return;
    (async () => {
      setLoadingDisbursementMethods(true);
      try {
        const response = await fetchJsonDynamic(
          "allDisbursementMethods",
          null,
          null,
          setupManagementUrl.uri,
        );
        const methods = response?.results ? JSON.parse(response.results) : [];
        setDisbursementMethods(methods.filter((m) => m.IsActive !== false));
      } catch (error) {
        console.error("Error fetching disbursement methods:", error);
      } finally {
        setLoadingDisbursementMethods(false);
      }
    })();
  }, [isCompanyLevel]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const BusinessDetailsActions = {
    entity: "Customer",
    edit: { key: "EDIT", button_name: "Edit", Show_Button: false, permission: "EditPermission" },
    add: { key: "ADD", button_name: "Add", Show_Button: false, permission: "CreatePermission" },
    dataObject: {
      ignoreFields: [
        "UpdateDate",
        "CreateDate",
        "LandMark",
        "HaveALocalAuthority",
        "VerifiedBy",
        "VerifiedDate",
      ],
      objectName: "customerBusinessDetailsById",
      parameters: { customerId: parseInt(Row.CustomerId) },
      inputObjectName: "customerBusinessDetails",
    },
  };
  const gurantorActions = {
    entity: "Customer",
    isSubright: true,
    edit: { key: "EDIT", button_name: "Edit", Show_Button: false, permission: "EditPermission" },
    add: { key: "ADD", button_name: "Add", Show_Button: false, permission: "CreatePermission" },
    dataObject: {
      ignoreFields: [
        "UpdateDate",
        "CreateDate",
        "VerifiedBy",
        "VerifiedDate",
        "HouseName",
        "FloorNumber",
        "RoomNumber",
        "Photo",
        "PhotoContentType",
        "IdFrontPhoto",
        "IdBackPhoto",
        "Town",
        "PermanentResidence",
        "Dob",
        "RelationshipToLoanee",
        "MaritalStatus",
      ],
      objectName: "getguarantorbycustomerid",
      parameters: { customerId: parseInt(Row.CustomerId) },
      inputObjectName: "guarantor",
    },
  };
  const LoanChargesActions = {
    entity: "Loans",
    isSubright: true,
    edit: { key: "EDIT", button_name: "Edit", Show_Button: false, permission: "EditPermission" },
    add: { key: "ADD", button_name: "Add", Show_Button: false, permission: "CreatePermission" },
    dataObject: {
      ignoreFields: ["UpdateDate", "CreateDate"],
      objectName: "allLoanChargesDetailsByLoanId",
      parameters: { loanId: parseInt(Row.Id) },
      inputObjectName: "parameters",
    },
  };

  // The exact success message approveRejectAssetLoan returns hasn't been
  // confirmed against the live backend, so — unlike the string-matched
  // "Loan Approved Successfully" check in LoanManagement's dialog — any
  // non-error response is treated as success here to avoid misreporting a
  // real approval as a failure over an unmatched message string.
  const submitDecision = async (flag, actionType) => {
    const swalOnTop = {
      didOpen: () => {
        const el = document.querySelector(".swal2-container");
        if (el) el.style.zIndex = "2000";
      },
    };

    // ── Step 1: reason prompt (always) ──────────────────────────────────
    const confirmation = await Swal.fire({
      title: "Are you sure?",
      text: `Do you want to ${actionType.toLowerCase()} this loan?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: `Yes, ${actionType.toLowerCase()} it!`,
      cancelButtonText: "No, keep it",
      input: "text",
      inputPlaceholder: `Enter the reason for ${actionType.toLowerCase()}`,
      inputValidator: (value) => {
        if (!value) return `You need to provide a reason for ${actionType.toLowerCase()}!`;
      },
      ...swalOnTop,
    });

    if (!confirmation.isConfirmed) return;

    // ── Step 2: OTP flow (approve only at company level when required) ────
    let otpValue = null;
    if (flag === 1 && isCompanyLevel) {
      let requireOtp = false;
      let requireOtpSms = true; // default: include SMS if email OTP enabled
      try {
        const settingsResponse = await fetchJsonDynamic(
          "loanSettings",
          null,
          null,
          loanManagementUrl.uri
        );
        if (settingsResponse?.results) {
          const settings = JSON.parse(settingsResponse.results) || [];
          const otpEmailSetting = settings.find(
            (s) =>
              s.Setting === "Require OTP For Loan Approval" ||
              s.Setting === "Require OTP For Loan Approval - Email" ||
              (typeof s.Setting === "string" && s.Setting.toLowerCase().includes("require otp") && !s.Setting.toLowerCase().includes("sms"))
          );
          const emailVal = otpEmailSetting?.Value?.toString()?.toLowerCase()?.trim();
          requireOtp = emailVal === "true" || emailVal === "yes" || emailVal === "1";

          const otpSmsSetting = settings.find(
            (s) => s.Setting === "Require OTP For Loan Approval - SMS"
          );
          const smsVal = otpSmsSetting?.Value?.toString()?.toLowerCase()?.trim();
          // If the SMS setting row is absent, default to true (same as backend)
          requireOtpSms = otpSmsSetting ? (smsVal === "true" || smsVal === "yes" || smsVal === "1") : true;
        }
      } catch (err) {
        console.error("Error checking loan OTP setting:", err);
      }

      if (requireOtp) {
        // Request OTP send
        const otpRequest = await executeAnyMutation(
          "requestApprovalOTP",
          { actionType: "Asset Loan Approval" },
          null,
          false,
          AssetFinanceUrl.uri
        );

        if (otpRequest?.error) {
          const errMsg = (() => {
            try { return JSON.parse(otpRequest.error)?.message || "Failed to send OTP."; }
            catch { return "Failed to send OTP."; }
          })();
          Swal.fire({ icon: "error", title: "OTP Error", text: errMsg, ...swalOnTop });
          return;
        }

        // Prompt user to enter the OTP
        const otpChannelText = requireOtpSms
          ? "An OTP has been sent to your registered email address and phone number."
          : "An OTP has been sent to your registered email address.";
        const otpInput = await Swal.fire({
          title: "Enter Approval OTP",
          text: `${otpChannelText} Enter it below to proceed.`,

          icon: "info",
          input: "text",
          inputPlaceholder: "Enter 6-digit OTP",
          showCancelButton: true,
          confirmButtonText: "Verify & Approve",
          cancelButtonText: "Cancel",
          inputValidator: (value) => {
            if (!value) return "OTP is required.";
            if (!/^\d{6}$/.test(value)) return "OTP must be a 6-digit number.";
          },
          ...swalOnTop,
        });

        if (!otpInput.isConfirmed) return;

        // Validate OTP
        const validateRes = await executeAnyMutation(
          "validateApprovalOTP",
          { otp: otpInput.value },
          null,
          false,
          AssetFinanceUrl.uri
        );

        if (validateRes?.error) {
          const errMsg = (() => {
            try { return JSON.parse(validateRes.error)?.message || "Invalid OTP."; }
            catch { return "Invalid OTP."; }
          })();
          Swal.fire({ icon: "error", title: "OTP Verification Failed", text: errMsg, ...swalOnTop });
          return;
        }
      }
    }

    // ── Step 3: Submit the decision ──────────────────────────────────────
    try {
      const mutationData = {
        loan_Id: Row.Id,
        flag,
        comments: confirmation.value,
        isBranch: approvalParameters.isBranch,
        isRegion: approvalParameters.isRegion,
        isFieldOfficer: approvalParameters.isFieldOfficer,
        isCompany: approvalParameters.isCompany,
        ...(isCompanyLevel ? { disbursementMethodId: Number(disbursementMethodSelected) } : {}),
      };

      const response = await executeAnyMutation(
        "approveRejectAssetLoan",
        mutationData,
        "param",
        false,
        AssetFinanceUrl.uri,
      );

      if (response?.error) {
        const errMsg = (() => {
          try { return JSON.parse(response.error)?.message || response.error; }
          catch { return typeof response.error === "string" ? response.error : "Request failed."; }
        })();
        Swal.fire({ icon: "error", title: "Error!", text: errMsg, ...swalOnTop });
        return;
      }

      refetchData();
      Swal.fire({ icon: "success", title: "Success!", text: `Loan ${actionType.toLowerCase()}d successfully.`, ...swalOnTop });
      onClose();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
        ...swalOnTop,
      });
    }
  };

  const handleApprove = () => submitDecision(1, "Approve");
  const handleReject = () => submitDecision(0, "Reject");

  const date = new Date();
  const formatter = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" disableEnforceFocus fullWidth>
      <Box sx={{ bgcolor: "background.paper", p: 4, overflowY: "auto", maxHeight: "90vh", maxWidth: "80vw" }}>
        <Typography variant="h5" gutterBottom>
          Asset Loan Decision
          <Divider sx={{ borderBottomWidth: 2, border: "1px solid #003049" }} orientation="horizontal" />
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <StyledCard variant="outlined">
              <Typography variant="subtitle1" gutterBottom>
                <strong>Customer Details</strong>
              </Typography>
              <Typography>
                Customer ID: <strong>{Row.CustomerId}</strong>
              </Typography>
              <Typography>
                Customer Name: <strong>{Row.FirstName} {Row.OtherNames}</strong>
              </Typography>
              <Typography>
                Phone: <strong>{Row.CustomerPhone}</strong>
              </Typography>
              <Typography>
                Branch: <strong>{Row.Name}</strong>
              </Typography>
            </StyledCard>

            <StyledCard variant="outlined">
              <Typography variant="h6" sx={{ color: "#003049", fontWeight: "bold" }}>
                Loan Details
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography>
                      Loan ID: <strong>{Row.Id}</strong>
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography>
                      Loan amount: <strong>{Row.LoanAmount?.toLocaleString()}</strong>
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography>
                      Installment amount: <strong>{Row.InstallmentAmount?.toLocaleString()}</strong>
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography>
                      Product: <strong>{Row.ProductName}</strong>
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography>
                      Borrow date:{" "}
                      <strong>
                        {Row.ApplicationDate ? formatter.format(new Date(Row.ApplicationDate)) : "N/A"}
                      </strong>
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </StyledCard>

            <CollateralSummary Row={Row} />

            {isCompanyLevel && (
              <StyledCard variant="outlined">
                <FormControl fullWidth required>
                  <InputLabel>Disbursement Method</InputLabel>
                  <Select
                    value={disbursementMethodSelected}
                    onChange={(e) => setDisbursementMethodSelected(e.target.value)}
                    disabled={loadingDisbursementMethods}
                  >
                    <MenuItem value="" disabled>
                      {loadingDisbursementMethods ? "Loading…" : "Select Disbursement Method"}
                    </MenuItem>
                    {disbursementMethods.map((method) => (
                      <MenuItem key={method.Id} value={method.Id}>
                        {method.MethodName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </StyledCard>
            )}

            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleApprove}
                disabled={isCompanyLevel && !disbursementMethodSelected}
                fullWidth
              >
                Approve
              </Button>
              <Button variant="outlined" color="error" onClick={handleReject} fullWidth>
                Reject
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} sm={8}>
            <Tabs value={activeTab} onChange={handleTabChange} sx={{ justifyContent: "space-between" }}>
              <Tab
                label="Business Details"
                sx={{ borderRadius: "0.75rem", border: "1px solid #003049", fontWeight: "bold", marginRight: "10px", mx: 0.5 }}
              />
              <Tab
                label="Guarantor Details"
                sx={{ borderRadius: "0.75rem", border: "1px solid #003049", fontWeight: "bold", mx: 0.5 }}
              />
              <Tab
                label="Loan Charges Details"
                sx={{ borderRadius: "0.75rem", border: "1px solid #003049", fontWeight: "bold", mx: 0.5 }}
              />
            </Tabs>

            {activeTab === 0 && (
              <StyledCard variant="outlined">
                <DynamicTableForJson base_url={customerservice.uri} actions={BusinessDetailsActions} />
              </StyledCard>
            )}
            {activeTab === 1 && (
              <StyledCard variant="outlined">
                <DynamicTableForJson base_url={customerservice.uri} actions={gurantorActions} />
              </StyledCard>
            )}
            {activeTab === 2 && (
              <StyledCard variant="outlined">
                <DynamicTableForJson base_url={AssetFinanceUrl.uri} actions={LoanChargesActions} />
              </StyledCard>
            )}
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  );
};

export default AssetLoanApprovalDialog;
